import React, {useContext, useEffect, useState} from "react"
import {Field, Form, Formik} from "formik"
import {useTranslation} from "next-i18next"
import CheckoutContext, {CheckoutData} from "../../context/CheckoutContext"
import CartContext from "../../context/CartContext"
import {useRouter} from "next/router"
import Link from "next/link"
import {DatepickerInputs} from "./fields/DatePickerField"
import Overview from "../checkout/Overview"
import {clarityEvents, ecommerceEvents, gaCategories, gaEvents} from "../../config/googleAnalytics/events"
import FormSelect from "../reusable/FormSelect/FormSelect"
import FormInput from "../reusable/FormInput/FormInput"
import Typography from "../reusable/Typography/Typography"
import GenderButtons from "../checkout/GenderButtons"
import AnalyticsContext from "../../context/AnalyticsContext"
import {calculateAge, isYearSelected} from "../../utility/calculateDateAndAge"
import {removeItemByValueFromCountryList} from "./data/countryList"
import {collectLead} from "../../utility/postHandler"
import {StepsIndicatorLine} from "../checkout/stepsIndicator"
import AccordionAnimation from "../reusable/components/AnimationComponent/AccordionAnimation"
import Image from "next/image"
import PhoneField from "./fields/PhoneField"
import Button from "../reusable/components/Button"
import CountrySelectionField from "./fields/CountrySelectionField"
import TermsAndConditionsCheckbox from "../checkout/TermsAndConditionsCheckbox"
import FirebaseContext from "../../context/FirebaseContext"
import HowItWorksCheckout from "../checkout/HowItWorksCheckout"
import {getCheckoutSchema, getPayOneTimeCheckoutSchema} from "../../validation/schemas/checkout"
import LanguageButtons from "../checkout/LanguageButtons"
import CheckBoxField from "./fields/CheckBoxField"
import {generateUUID} from "../../utility/UUID"
import {events, reformatPhoneNumber} from "../../utility/CheckoutHelpers"
import organizationContext from "../../context/OrganizationContext"
import {formatPrice} from "../../utility/Helper"
import {fetchPost} from "../../firebaseAdmin/fetchPost"

export enum OverviewButtonText {
	continue = "Weiter",
	submit = "Absenden"
}
declare global {
    interface Window {
        _hsq?: any[],
    }
}

interface MyFormValues {
    email: string,
    gender: string,
    firstname: string,
    lastname: string,
    id_upload_link?: string,
    contract_type?: string,
    permit_type?: string,
    date_of_entry?: string,
	permit_valid_until?: string,
    phone: string|number,
	prefix: string,
    date_of_birth?: string|null,
    // address: string,
    street: string,
    street_number: number|string,
    city: string,
    zip: string,
    // delivery_street_address: string,
    secondary_address_street: string,
    secondary_address_street_number: number,
    secondary_address_city: string,
    secondary_address_zip: string,
	language_selected: string,
    unique_referral_code?: string,
    no_debt_enforcement?: string,
    onShipAddress?: boolean
    google_user_id?: string,
    google_client_id?: string,
    country_choice?: string | null,
	id_type?: string
	date_of_birth_month?: string|number
	date_of_birth_day?: string|number
	date_of_birth_year?: string|number
	date_of_entry_month?: string|number
	date_of_entry_day?: string|number
	date_of_entry_year?: string|number
	permit_valid_until_month?: string|number
	permit_valid_until_day?: string|number
	permit_valid_until_year?: string|number
	all_agb?: string|boolean
	firebase_contact_uid?: string
}

const CheckoutFormContactDetails: React.FC<{ lineStepsIndicator: boolean, payOneTime?: boolean }> = ({lineStepsIndicator, payOneTime = false}) => {
	const {t} = useTranslation(["common", "checkout"])
	const checkoutSchema = getCheckoutSchema(t, payOneTime)
	const checkoutSchemaPayOneTime = getPayOneTimeCheckoutSchema(t, payOneTime)
	const {data, setData, checkoutPostHandler} = useContext(CheckoutContext)
	const {uuid, clientId} = useContext(AnalyticsContext)
	const {items, clearCart} = useContext(CartContext)
	const {user} = useContext(FirebaseContext)
	const [checkoutError, setCheckoutError] = useState(false)
	const router = useRouter()
	const {locale, pathname} = router
	const [isOverviewOpen, setIsOverviewOpen] = useState(false)
	const currentUrl = router.asPath
	const {reactGA} = useContext(AnalyticsContext)
	const [initialValues, setInitialValues] = useState<any | null>(null)
	const pageUriBase = `${process.env.NEXT_PUBLIC_APP_URL}${pathname}`
	const [pageUri, setPageUri] = useState(pageUriBase)
	const {organizationSlug} = useContext(organizationContext)

	// Setup tracking url for utm pourposes
	useEffect(() => {
		if (typeof window !== "undefined") {
			const utmData = localStorage.getItem("checkoutUtm")
			setPageUri(`${pageUriBase}${utmData}`)
		}
	}, [])

	useEffect(() => {
		if (data) {
			setInitialValues({
				defaultInitialValues: {
					email: data.contact?.email,
					gender: data.contact?.gender || "Frau",
					firstname: data.contact?.firstname,
					lastname: data.contact?.lastname,
					id_upload_link: "",
					contract_type: data.contact?.contract_type || "Festangestellt",
					permit_type: data.contact?.permit_type || "Swiss",
					date_of_entry: data.contact?.date_of_entry || "",
					permit_valid_until: data.contact?.permit_valid_until || "",
					phone: data.contact?.phone || "",
					prefix: data.contact?.prefix || "CH (+41)",
					date_of_birth: data.contact?.date_of_birth || "",
					// address: data.contact?.address,
					street: data.contact?.street,
					street_number: data.contact?.street_number,
					city: data.contact?.city,
					zip: data.contact?.zip,
					// delivery_street_address: data.contact?.delivery_street_address,
					secondary_address_street: data.contact?.secondary_address_street,
					secondary_address_street_number: data.contact?.secondary_address_street_number,
					secondary_address_city: data.contact?.secondary_address_city,
					secondary_address_zip: data.contact?.secondary_address_zip,
					language_selected: locale.toUpperCase(),
					unique_referral_code: data.contact?.unique_referral_code,
					no_debt_enforcement: "",
					onShipAddress: false,
					all_agb: false,
					google_user_id: uuid,
					google_client_id: `GA-${clientId}`,
					country_choice: "",
					id_type: "id",
					date_of_birth_month: data.contact?.date_of_birth_month || "01",
					date_of_birth_day: data.contact?.date_of_birth_day || "",
					date_of_birth_year: data.contact?.date_of_birth_year || "",
					permit_valid_until_month: data.contact?.permit_valid_until_month || "01",
					permit_valid_until_day: data.contact?.permit_valid_until_day || "",
					permit_valid_until_year: data.contact?.permit_valid_until_year || "",
					date_of_entry_month: data.contact?.date_of_entry_month || "01",
					date_of_entry_day: data.contact?.date_of_entry_day || "",
					date_of_entry_year: data.contact?.date_of_entry_year || "",
					firebase_contact_uid: user ? user.uid : ""
				} as MyFormValues,
				oneTimePaymentInitialValues: {
					email: data.contact?.email,
					gender: data.contact?.gender || "Frau",
					firstname: data.contact?.firstname,
					lastname: data.contact?.lastname,
					id_upload_link: "",
					phone: data.contact?.phone || "",
					prefix: data.contact?.prefix || "CH (+41)",
					// address: data.contact?.address,
					street: data.contact?.street,
					street_number: data.contact?.street_number,
					city: data.contact?.city,
					zip: data.contact?.zip,
					// delivery_street_address: data.contact?.delivery_street_address,
					secondary_address_street: data.contact?.secondary_address_street,
					secondary_address_street_number: data.contact?.secondary_address_street_number,
					secondary_address_city: data.contact?.secondary_address_city,
					secondary_address_zip: data.contact?.secondary_address_zip,
					language_selected: locale.toUpperCase(),
					unique_referral_code: data.contact?.unique_referral_code,
					onShipAddress: false,
					all_agb: false,
					google_user_id: uuid,
					google_client_id: `GA-${clientId}`,
					firebase_contact_uid: user ? user.uid : ""
				} as MyFormValues
			})
		}
	}, [data])

	let totalPrice = 0
	items?.forEach(item => {
		totalPrice += parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
	})

	const priceWithDiscountCode = data?.resultingPrice || totalPrice

	return (initialValues &&
		<Formik
			initialValues={payOneTime ? initialValues.oneTimePaymentInitialValues : initialValues.defaultInitialValues}
			validationSchema={payOneTime ? checkoutSchemaPayOneTime : checkoutSchema}
			validateOnMount={false}
			validateOnBlur={true}
			validateOnChange={true}
			onSubmit={async (values, actions) => {
				try {
					setCheckoutError(false)

					const reformattedPhoneNumber: any | null = await reformatPhoneNumber(values.prefix, values.phone)
					const reformattedContactValues = {
						...values,
						phone: reformattedPhoneNumber?.number || (values.phone).toString()
					}

					// Send checkout to API
					const checkoutPostHandlerResponse = await checkoutPostHandler(reformattedContactValues, {
						...data,
						deals: data.deals.map(deal => {
							return {
								...deal,
								full_payment: payOneTime ? "true" : "false"
							}
						})
					}, user?.uid, data.insurance, data.planLength, items, payOneTime, router.query.sponsorMeToken as string || null, organizationSlug).then(async response => {
						return {
							response
						}
					}).catch(e => {
						// Log event
						reactGA?.event({
							category: gaCategories.checkout,
							action: gaEvents.checkoutFailedA,
							label: gaEvents.checkoutFailedA,
							nonInteraction: true
						})
						console.error("Error: ", e)
						return e
					})

					const {response} = checkoutPostHandlerResponse
					const {docId, updateDocToken} = response

					// Log events
					events(data, reactGA)

					// Store values in contact data of checkout object
					setData(() => {
						return {
							...data,
							contact: {...values}
						} as CheckoutData
					})

					if (response.status === 201) {
						// Log event
						reactGA?.event({
							category: gaCategories.checkout,
							action: gaEvents.checkoutSuccessA,
							label: gaEvents.checkoutSuccessA,
							nonInteraction: true
						})

						try {
							reactGA?.gtag(ecommerceEvents.event, ecommerceEvents.purchase, {
								currency: ecommerceEvents.CHF,
								value: totalPrice,
								items: data.deals.map(deal => {
									const {article_number_2, e_bike_choice, e_bike_price} = deal
									return {
										item_id: article_number_2,
										item_name: e_bike_choice,
										price: e_bike_price
									}
								})})
						} catch (e) {
							console.error("Could not send purchase event:", e)
						}

						if (data?.discountCode) {
							await fetchPost("/api/delete-discount-code", {discountCode: data?.discountCode})
						}

						// Redirect to upload id
						const redirectUrl = payOneTime ?
							`${t("/checkout/bestaetigung")}/${docId}/${updateDocToken}` :
							`${t("/checkout/meine-angaben/identitaetspruefung")}/${encodeURIComponent(docId)}/${updateDocToken}`

						router.push(redirectUrl).then(() => {
							// Clear cart
							clearCart()

							// Log event
							reactGA?.event({
								category: gaCategories.checkout,
								action: gaEvents.checkoutStepCompleted,
								label: gaEvents.checkoutStepCompleted,
								nonInteraction: true,
								value: 3
							})

							// Log event
							reactGA?.event({
								category: gaCategories.checkout,
								action: gaEvents.checkoutStep3Completed,
								label: gaEvents.checkoutStep3Completed,
								nonInteraction: true
							})
						})
					} else {
						console.error("Sending checkout data failed with status: ", response.status)
						setCheckoutError(true)
						// Log event
						reactGA?.event({
							category: gaCategories.checkout,
							action: gaEvents.checkoutFailedA,
							label: gaEvents.checkoutFailedA,
							nonInteraction: true
						})
					}

					actions.setSubmitting(false)
				} catch (e) {
					reactGA?.event({
						category: gaCategories.checkout,
						action: gaEvents.checkoutFailedA,
						label: gaEvents.checkoutFailedA,
						nonInteraction: true
					})
					console.error(e)
				}
			}}
		>
			{({errors, touched, handleSubmit, values, isSubmitting, submitCount}) => {
				return (
					<Form className="row mt-5"
						onSubmit={handleSubmit}>
						{!lineStepsIndicator && !payOneTime && <h1 className="d-block d-md-none text-bold text-center"
							style={{
								fontSize: "1.5rem"
							}}>
							{t("Checkout")}
						</h1>}
						<div className={"col-12 col-md-4 d-block d-md-none"}>
							<div className="col-auto d-flex align-items-center mb-3">
								<Image src={"/assets/icons/checkout/lock-v2.svg"}
									width={16}
									height={20}/>
								<Typography className="ms-4 mt-1"
									variant="heading1"
									semanticTag="span">{t("secure-checkout")}</Typography>
							</div>
							<div onClick={() => {
								setIsOverviewOpen(isOpen => !isOpen)
							}}
							className={`d-flex justify-content-center bg-grey p-3 ${isOverviewOpen ? "" : "mb-2"}`}
							style={{borderRadius: "16px", marginBottom: "-12px"}}>
								<Typography className="me-1"
									variant="bodyLgBold"
									semanticTag="span">{`${t("Übersicht")}:`}</Typography>
								<span className="d-flex aligin-itmes-top g-0">
									<Typography className="me-3"
										variant="bodyLgBold"
										semanticTag="span">
										{t(`CHF ${priceWithDiscountCode.toFixed(2)}`)}
									</Typography>
									{isOverviewOpen ?
										<Image src={"/assets/icons/checkout/chevron-up-v2.svg"}
											width={12}
											height={7} /> :
										<Image src={"/assets/icons/checkout/chevron-down-v2.svg"}
											width={12}
											height={7}/>
									}
								</span>
							</div>
							<AccordionAnimation inProp={isOverviewOpen}>
								<Overview isNewDesign
									payOneTime={payOneTime}
									hideButton={true}
									buttonText={`${OverviewButtonText.continue}`}>
									{items.map((item, index) => {
										const frame = item.product.attributes?.find(e => e.name === "Rahmenvarianten")?.terms[0].name
										const productUrl = ` /${t("produkt")}/${item.product.slug}`
										const price = parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
										const correspondingDeal = data.deals?.find(deal => deal.bikeId === item.product.id)
										const pricePerMonth = (price - (correspondingDeal.down_payment_amount || 0)) / (data?.planLength || 36)

										return (
											// <Link href={productUrl} key={`${item.product.id}-${index}`}>
											<div key={generateUUID()}
												className="row align-items-center mb-1 pointer ">
												<div className={"col-12"}>
													<Link href={productUrl}
														key={`${item.product.id}-${index}`}>
														<div className={"row align-items-center bg-white p-1"}
															style={{borderRadius: "16px"}}>
															<div className={"col-4 p-0"}>
																<img src={item.product.images[0]?.src}
																	width={"100%"}
																	height={"auto"}/>
															</div>
															<div className={"col-8"}>
																{/* <div>
																	<Typography variant="bodyLg" semanticTag="span"
																		className={"text-secondary text-uppercase"}>
																		{brand}
																	</Typography>
																</div> */}
																{/* <div> */}
																<Typography className="lh-1"
																	variant="bodySmBold"
																	semanticTag="h2">
																	{item.product.name}
																</Typography>
																{/* </div> */}
																<div>
																	<Typography variant="bodySmBold"
																		semanticTag="span">
																		{"CHF"}
																	</Typography>
																	<Typography variant="heading1"
																		semanticTag="span">
																		{` ${pricePerMonth.toFixed(2)} `}
																	</Typography>
																	<Typography variant="bodySmBold"
																		semanticTag="span">
																		{"/m"}
																	</Typography>
																</div>
																<Typography className="text-secondary"
																	variant="bodySm"
																	semanticTag="p">
																	{`${t("Gesamtkosten")}: ${formatPrice(price)} `}
																</Typography>
																{/* <div>
																	<Typography variant="bodyLg" semanticTag="span"
																		className={"text-secondary"}>
																		{frame && `${item.selectedSize}, ${frame}`}
																	</Typography>
																</div> */}
															</div>
														</div>
													</Link>
													<div>
														<Typography variant="bodyLg"
															semanticTag="p"
															className={"mt-2"}>
															{frame && `${item.selectedSize}, ${frame}`}
														</Typography>
													</div>
												</div>
											</div>
										)
									}
									)}
								</Overview>
							</AccordionAnimation>
							<div className="mt-4">
								{lineStepsIndicator && <StepsIndicatorLine isNewDesign
									activeStep={1}/>}
							</div>
						</div>
						<div className="col-12 col-md-8 mt-sm-5">
							{!lineStepsIndicator && <Typography variant="heading2"
								semanticTag="h1"
								className="mb-3">
								{t("Meine Kontaktangaben")}
							</Typography>}
							{payOneTime &&
							<div>
								<div style={{height: 4, backgroundColor: "#DEE2E6"}}
									className={"text-primary rounded  position-relative"}>
									<div className={"position-absolute start-0 top-0 bg-primary rounded h-100 w-100"}></div>
								</div>
								<HowItWorksCheckout/>
							</div>
							}
							{lineStepsIndicator &&
							<div className="col-11 d-none d-md-block">
								<StepsIndicatorLine activeStep={1}/>
							</div>}
							<div className="col-12 col-lg-11">
								<div className="row mt-3">
									<div className="col-12 col-lg-5">
										<GenderButtons/>
									</div>
								</div>
								<div className="row mt-3 justify-content-center">
									<div className="col-12 col-lg-5">
										<FormInput isNewDesign
											className="pb-3"
											cypressClassName="data-cypress-personal-data-firstname"
											errors={errors}
											touched={touched}
											name="firstname"
											label={t("Vorname")}
											required
											errColor="dark"
											checkout/>
									</div>
									<div className="col-12 col-lg-7">
										<FormInput isNewDesign
											className="pb-3 "
											cypressClassName="data-cypress-personal-data-lastname"
											errors={errors}
											touched={touched}
											name="lastname"
											required
											errColor="dark"
											label={t("Nachname")}
											checkout/>
									</div>
								</div>
								<div className="row mt-lg-3 justify-content-center">
									<PhoneField/>

									<div className="col-12 col-lg-7">
										<FormInput isNewDesign
											onBlur={() => {
												if (
													typeof errors.email === "undefined" &&
                                                typeof errors.lastname === "undefined" &&
                                                typeof errors.firstname === "undefined" &&
                                                values.email !== "") {
													collectLead(currentUrl, locale)
												}
											}}
											className="pb-3"
											cypressClassName="data-cypress-personal-data-email"
											errors={errors}
											touched={touched}
											name="email"
											required
											errColor="dark"
											label={t("E-Mail")}
											checkout/>
									</div>
								</div>
								<div className="row mt-lg-3  justify-content-center">
									<div className="col-12 col-lg-8">
										<FormInput isNewDesign
											className="pb-3"
											cypressClassName="data-cypress-personal-data-street"
											errors={errors}
											touched={touched}
											name="street"
											required
											errColor="dark"
											label={t("Strasse")}/>
									</div>
									<div className="col-12 col-lg-4">
										<FormInput isNewDesign
											className="pb-3"
											cypressClassName="data-cypress-personal-data-nbr"
											errors={errors}
											touched={touched}
											name="street_number"
											required
											errColor="dark"
											label={t("Nr.")}
											type={"text"}/>
									</div>
								</div>
								<div className="row mt-lg-3 justify-content-center">
									<div className="col-12 col-lg-5">
										<FormInput isNewDesign
											className="pb-3"
											cypressClassName="data-cypress-personal-data-zip"
											errors={errors}
											touched={touched}
											name="zip"
											required
											errColor="dark"
											label={t("Postleitzahl")}/>
									</div>
									<div className="col-12 col-lg-7">
										<FormInput isNewDesign
											className="pb-3"
											cypressClassName="data-cypress-personal-data-city"
											errors={errors}
											touched={touched}
											name="city"
											required
											errColor="dark"
											label={t("Ort/Stadt")}/>
									</div>
								</div>
								{payOneTime || <CheckBoxField name={"onShipAddress"}>
									<Typography variant="bodyLg">{t("Ich bin innerhalb der letzten zwei Jahre umgezogen.")}</Typography>
								</CheckBoxField>
								}
								{values.onShipAddress ? (
									payOneTime || <>
										<p>
											<Typography variant={"bodyLgBold"}
												semanticTag={"div"}>
												{t("checkout-form-old-address-subtitle")}
											</Typography>
											<Typography variant={"bodyLg"}
												semanticTag={"div"}
												className={"mb-4"}>
												{t("checkout-form-old-address-text")}
											</Typography>
										</p>
										<div className="row mt-lg-3 justify-content-center">
											<div className="col-12 col-lg-8">
												<FormInput isNewDesign
													errorDisplay={false}
													className="pb-3"
													errors={errors}
													touched={touched}
													name="secondary_address_street"
													required
													errColor="dark"
													label={t("Strasse")}/>
											</div>
											<div className="col-12 col-lg-4">
												<FormInput isNewDesign
													errorDisplay={false}
													className="pb-3"
													errors={errors}
													touched={touched}
													name="secondary_address_street_number"
													required
													errColor="dark"
													label={t("Nr.")}
													type={"number"}/>
											</div>
										</div>
										<div className="row mt-lg-3 justify-content-center">
											<div className="col-12 col-lg-5">
												<FormInput isNewDesign
													errorDisplay={false}
													className="pb-3"
													errors={errors}
													touched={touched}
													name="secondary_address_zip"
													required
													errColor="dark"
													label={t("Postleitzahl")}/>
											</div>
											<div className="col-12 col-lg-7">
												<FormInput isNewDesign
													errorDisplay={false}
													className="pb-3"
													errors={errors}
													touched={touched}
													name="secondary_address_city"
													required
													errColor="dark"
													label={t("Ort/Stadt")}/>
											</div>
										</div>
									</>) : <></>}
								{payOneTime || <>
									{/* Create a component specific for CHECKOUT Birtday field */}
									<div className="row mt-3">
										<div className="col-12 col-md-5 col-lg-5">
											<DatepickerInputs name="date_of_birth"
												label={t("Geburtstag")} />
										</div>
										{/* Move this logic to Dynamic generator */}
										{touched.date_of_birth && !values.date_of_birth ?
											<div>{errors.date_of_birth}</div> : values.date_of_birth ? null :
												<Typography variant="bodyLg"
													semanticTag="div">{t("Bitte gib dein Geburtsdatum an")}</Typography>}
										{values.date_of_birth && !isYearSelected(values.date_of_birth, "/") &&
									<Typography variant="bodyLg"
										semanticTag="div"
										className="text-dark">{t("clarify-age-warning", {ns: "checkout"})}</Typography>
										}
										{isYearSelected(values.date_of_birth, "/") && (calculateAge(values.date_of_birth, "/") <= 18) &&
									<Typography variant="bodyLg"
										semanticTag="div"
										className="text-dark">{t("25-years-warning", {ns: "checkout"})}</Typography>}
									</div>
									{/* Use Component above for CHeckout Birt */}
									<div className="row mt-3 mb-2">
										<label className="col-12 bg-white text-reg text-bold mb-3"
											htmlFor="email">{t("Sprache")}*</label>

										<div className="d-md-none"> <LanguageButtons/>
											{errors.language_selected && touched.language_selected ? (
												<div style={{color: "red"}}>{errors.language_selected}</div>
											) : null}
										</div>

										<div className="row d-none d-md-block"
											role="group">
											<div className="col-12">
												<label className="col-12 container-radio">
													<Field type="radio"
														name="language_selected"
														value="DE"/>
													<span className="radio radio-circ"></span>
													{t("Deutsch")}
												</label>
											</div>
											<div className="col-12">
												<label className="col-12 container-radio">
													<Field type="radio"
														name="language_selected"
														value="FR"/>
													<span className="radio radio-circ"></span>
													{"Français"}
												</label>
											</div>
											<div className="col-12">
												<label className="col-12 container-radio">
													<Field type="radio"
														name="language_selected"
														value="ENG"/>
													<span className="radio radio-circ"></span>
													{"English"}
												</label>
											</div>
											<div className="col-12">
												<label className="col-12 container-radio">
													<Field type="radio"
														name="language_selected"
														value="ITA"/>
													<span className="radio radio-circ"></span>
													{"Italiano"}
												</label>
											</div>
											<div className="col-12">
												<label className="col-12 container-radio">
													<Field type="radio"
														name="language_selected"
														value="POR"/>
													<span className="radio radio-circ"></span>
													{"Português"}
												</label>
											</div>
											{errors.language_selected && touched.language_selected ? (
												<div style={{color: "red"}}>{errors.language_selected}</div>
											) : null}
										</div>

									</div>
									{/* Permit type */}
									<div className="row mt-3 justify-content-center">
										<div className="col-12 mb-3">
											<Field outLabel
												name="contract_type"
												label="Arbeitsverhältnis"
												required
												component={FormSelect}
												t={t}
												options={[{
													label: "Festangestellt",
													value: "Festangestellt"
												}, {label: "Befristet", value: "Befristet"}, {
													label: "Arbeitsunfähigkeit",
													value: "Arbeitsunfähigkeit"
												}, {label: "AHV-Rente", value: "AHV-Rente"}, {
													label: "Selbständig",
													value: "Selbständig"
												}]}/>
										</div>
										<div className="col-12">
											<Field outLabel
												name="permit_type"
												label="Staatsbürgerschaft oder Aufenthaltsgenehmigung"
												required
												component={FormSelect}
												t={t}
												options={[{label: "Schweizer Staatsbürgerschaft", value: "Swiss"},
													{label: "B-Bewilligung", value: "B Permit"},
													{label: "C-Bewilligung", value: "C Permit"}]}/>
										</div>
									</div>
									{(values.permit_type === "B Permit" || values.permit_type === "C Permit") && (
										<div className="row mt-3">
											<div className="col-12 col-lg-6">
												<DatepickerInputs name="date_of_entry"
													label={t("Einreisedatum")} />
											</div>
											<div className="col-12 col-lg-6">
												<DatepickerInputs name="permit_valid_until"
													label={t("permit-valid-until-label")} />
											</div>
											<div className="col-12 col-lg-6">
												<CountrySelectionField required={true}
													placeholder={t("country-select-placeholder", {ns: "checkout"})}
													name="country_choice"
													label={t("country-label")}
													errors={errors}
													touched={touched}
													options={removeItemByValueFromCountryList("Switzerland")}/>
											</div>
										</div>
									)}
									{/* End permit type */}
								</>}
								<div className="d-block d-md-none">
									<div className="row">
										<div className={"col-12"}>
											<div className={"d-grid"}>
												<TermsAndConditionsCheckbox/>
											</div>
										</div>
									</div>
									<div className="row m-1">
										{Object.keys(errors).length > 0 && submitCount > 0 ?
											<div className={"alert alert-danger mt-2"}>
												{t("Bitte überprüfe das Formular. Eines oder mehrere Felder sind noch nicht ausgefüllt.")}
											</div> :
											null}
										<Button className= "btn btn-primary btn-lg py-3 data-cypress-personal-data-confirm-personal-data"
											label={t(`${payOneTime ? OverviewButtonText.submit : OverviewButtonText.continue}`)}
											disabled={Boolean(isSubmitting)}
											type={"submit"}
											onClick={() => {
												if (typeof window !== "undefined" && !payOneTime) {
													window.clarity("set", clarityEvents.purchase, "User Made a purchase")
												}
											}}
											color="#212529"
											textColor="#212529"
											bgColor="#FEC04D"
											spinnerColor={"#212529"}
											isLoading={isSubmitting}
										/>
										{checkoutError &&
										<Typography className="text-danger text-center mt-4"
											semanticTag="div"
											variant="heading2">{t("form-submit-error-message", {ns: "checkout"})}</Typography>}
									</div>
								</div>
							</div>
						</div>
						<div className={"col-12 col-md-4 mt-5 d-none d-md-block"}>
							<Overview payOneTime={payOneTime}
								isCheckoutError={checkoutError}
								hideButton={true}
								buttonText={`${OverviewButtonText.continue}`}>
								{items.map((item, index) => {
									const brand = item.product.attributes?.find(e => e.slug === "brand")?.terms[0].name
									const frame = item.product.attributes?.find(e => e.slug === "frame_variant")?.terms[0].name

									const productUrl = ` /${t("produkt")}/${item.product.slug}`

									const isLast = index === items.length - 1

									return (
										<Link href={productUrl}
											key={`${item.product.id}-${index}`}>
											<div
												className={`row align-items-center mb-2 pointer ${isLast ? null : "border-bottom"} pb-3 pt-3`}>
												<div className={"col"}>
													<div className={"row align-items-center"}>
														<div className={"col-3"}>
															<img src={item.product.images[0]?.src}
																width={"100%"}
																height={"auto"}/>
														</div>
														<div className={"col"}>
															<div>
																<span className={"text-secondary text-uppercase"}>
																	{brand}
																</span>
															</div>
															<div>
																<span className={"text-bold"}>
																	{item.product.name}
																</span>
															</div>
															<div>
																<span className={"text-secondary"}>
																	{frame && `${item.selectedSize}, ${frame}`}
																</span>
															</div>
														</div>
													</div>
												</div>
											</div>
										</Link>
									)
								}
								)}
							</Overview>
						</div>
					</Form>
				)
			}}
		</Formik>
	)
}

export default CheckoutFormContactDetails
